<template>
  <section
    v-bind="attrs"
    ref="sectionRef"
    :id="`nav-section-${sectionId}`"
    :class="{
      'mb-8 mt-5': !inline,
      navigation: navigationName !== undefined,
      'ml-4': indent,
    }"
  >
    <div
      class="mb-3 flex space-x-2 md:px-0"
      v-if="!indent"
      :class="{
        'px-4': !inline,
      }"
    >
      <template v-if="labelProxy !== ''">
        <slot name="heading">
          <Component
            :is="headingElement"
            class="font-semibold"
            :class="{
              'text-base sm:text-lg lg:text-xl': !inline,
              'text-base sm:text-lg': inline && !labelHeading,
            }"
          >
            {{ labelProxy }}
          </Component>
        </slot>
      </template>
    </div>
    <div class="relative" :class="classes">
      <DsAlert
        v-if="optionalInputError !== null"
        type="error"
        label="Es ist ein Fehler aufgetreten"
        class="custom-section-error mb-4"
      >
        {{ optionalInputError }}
      </DsAlert>
      <slot></slot>
    </div>
  </section>
</template>

<script setup lang="ts">
import { DsAlert } from '@demvsystems/design-components';
import get from 'lodash/get';
import { computed, type ComputedRef, onUnmounted, ref, useAttrs } from 'vue';

import { VALIDATION_MAP_KEYS } from '@/features/validation/validationMapKeys';
import { useValidation } from '@/utils/form';
import { useSectionNavigator } from '@/utils/useSectionNavigator/useSectionNavigator';
import { generateUUID } from '@/utils/uuid/uuid';
import { useIntersectionObserver } from '@vueuse/core';
import { SECTION } from '@/components/layout/types';

interface Props {
  name: SECTION;
  label?: string | ComputedRef<string>;
  inline?: boolean;
  noBackground?: boolean;
  noPadding?: boolean;
  navigationName?: string;
  navigationIcon?: string;
  navigationIndex?: number;
  navigationSortingOrder?: number;
  navigationShowCheckIfSelected?: boolean;
  inputErrorPath?: string;
  labelHeading?: boolean;
  indent?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  label: '',
  inline: false,
  noBackground: false,
  noPadding: false,
  navigationName: undefined,
  navigationIcon: undefined,
  navigationIndex: undefined,
  navigationSortingOrder: undefined,
  navigationShowCheckIfSelected: false,
  inputErrorPath: undefined,
  labelHeading: false,
  indent: false,
});

const attrs = useAttrs();

const labelProxy = computed(() => {
  if (typeof props.label === 'object') {
    return props.label.value;
  }

  return props.label;
});

const classes = computed(() => {
  const modifiers = [];
  if (!props.inline && !props.noBackground) {
    modifiers.push('shadow-md');
    modifiers.push('bg-white');
  }

  if (!props.inline) {
    modifiers.push('rounded-md');
  }

  if (!props.noPadding && !props.inline) {
    modifiers.push('p-5');
  }

  return modifiers;
});

const headingElement = computed(() => (!props.inline ? 'h5' : 'h6'));

const sectionId = generateUUID();

const sectionRef = ref<HTMLDivElement | null>();
const sectionNavigator = useSectionNavigator();
const addToNavigator =
  props.navigationName !== undefined && props.navigationIcon !== undefined;

let stopIntersecting: undefined | (() => void);

if (addToNavigator) {
  const sortingOrder =
    props.navigationSortingOrder ?? sectionNavigator.genericSortingOrder.value;

  sectionNavigator.addSection(
    props.navigationName,
    sectionId,
    props.navigationIcon,
    sortingOrder,
    props.inputErrorPath ?? '',
    props.navigationIndex,
    props.navigationShowCheckIfSelected,
  );

  const intersectionObserver = useIntersectionObserver(
    sectionRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting) {
        sectionNavigator.visibleSection.value = sectionId;
      }
    },
    {
      rootMargin: '-55% 0% -55% 0%',
      threshold: 0,
    },
  );

  stopIntersecting = intersectionObserver.stop;
}

const { errors } = useValidation(VALIDATION_MAP_KEYS.INPUT);

const optionalInputError = computed(() => {
  if (props.inputErrorPath === undefined) return null;

  const error = get(errors.value, props.inputErrorPath, []);

  if (error.length > 0) {
    return get(errors.value, props.inputErrorPath).join(' ');
  }

  return null;
});

onUnmounted(() => {
  useSectionNavigator().removeSection(sectionId);
  if (stopIntersecting !== undefined) {
    stopIntersecting();
  }
});
</script>
